import { PatientInfo, SubtestScores, CompositeScores } from '../types';
import { calculateDiscrepancies, getScoreInterpretation } from './helpers';
import { WISCV_FACTS_PLACEHOLDER } from './interpretationPrompt';

/**
 * Pure prompt-templating engine for the WISC-V interpretation prompts.
 * No React, no side effects — fully unit-testable.
 *
 * Supported placeholders:
 *   {PATIENT_NAME}, {VCI_SCORE}, {VSI_SCORE}, {FRI_SCORE}, {WMI_SCORE},
 *   {PSI_SCORE}, {FSIQ_SCORE}, {SUBTEST_SCORES}, {DISCREPANCIES},
 *   {WISCV_FACTS} — the fact block for the v2.1 prompt (buildInterpretationFacts)
 */

// ---------------------------------------------------------------------------
// Prompt v2.1: facts computed by code (see interpretationPrompt.ts)
// ---------------------------------------------------------------------------

/** Index names used in the fact block (order = order in the prompt). */
const FACT_INDEX_NAMES: Record<string, string> = {
  fsiq: 'Pełna Skala (FSIQ)',
  vci: 'Rozumienie Słowne (VCI)',
  vsi: 'Przetwarzanie Wzrokowo-Przestrzenne (VSI)',
  fri: 'Rozumowanie Płynne (FRI)',
  wmi: 'Pamięć Robocza (WMI)',
  psi: 'Szybkość Przetwarzania (PSI)',
};
const PRIMARY_INDEXES = ['vci', 'vsi', 'fri', 'wmi', 'psi'] as const;

/** Canonical 10 primary subtests (order of the legacy score form) → Polish name + index. */
export const FACT_SUBTESTS: ReadonlyArray<{ key: string; name: string; index: string }> = [
  { key: 'block_design', name: 'Klocki', index: 'vsi' },
  { key: 'similarities', name: 'Podobieństwa', index: 'vci' },
  { key: 'matrix_reasoning', name: 'Rozumowanie matrycowe', index: 'fri' },
  { key: 'digit_span', name: 'Powtarzanie liczb', index: 'wmi' },
  { key: 'coding', name: 'Kodowanie', index: 'psi' },
  { key: 'vocabulary', name: 'Słownictwo', index: 'vci' },
  { key: 'figure_weights', name: 'Wagi figur', index: 'fri' },
  { key: 'visual_puzzles', name: 'Puzzle wizualne', index: 'vsi' },
  { key: 'picture_span', name: 'Rozpiętość obrazkowa', index: 'wmi' },
  { key: 'symbol_search', name: 'Szukanie symboli', index: 'psi' },
];

const validScore = (v: unknown): number | null => {
  if (v === undefined || v === null || v === '') return null;
  const n = parseInt(String(v), 10);
  return Number.isNaN(n) || n <= 0 ? null : n;
};
const abbr = (key: string) => key.toUpperCase();

export interface InterpretationFactsInput {
  name?: string;
  subtestScores: Record<string, string | number | undefined>;
  compositeScores: Record<string, string | number | undefined>;
}

/**
 * Builds the DANE block for the v2.1 prompt: index levels with the app's labels,
 * highest/lowest index, range, differences ≥ 15 pts (explicitly NOT called
 * statistically significant — the app has no critical-value tables), subtests
 * as context and the list of missing subtests. Contains no norms, percentiles or CIs.
 */
export const buildInterpretationFacts = ({ name, subtestScores, compositeScores }: InterpretationFactsInput): string => {
  const indexLines = ['fsiq', ...PRIMARY_INDEXES].map((k) => {
    const s = validScore(compositeScores[k]);
    return s === null
      ? `- ${FACT_INDEX_NAMES[k]}: brak wyniku`
      : `- ${FACT_INDEX_NAMES[k]}: ${s} — poziom: ${getScoreInterpretation(s, 'composite').toLowerCase()}`;
  });

  const scored = PRIMARY_INDEXES
    .map((k) => [k, validScore(compositeScores[k])] as const)
    .filter((e): e is readonly [typeof PRIMARY_INDEXES[number], number] => e[1] !== null);

  const lines: string[] = [
    'DANE (policzone przez program; nie dodawaj innych):',
    `Imię: ${name?.trim() || 'Dziecko'}`,
    'Wskaźniki (wyniki standardowe, średnia 100, SD 15):',
    ...indexLines,
  ];

  if (scored.length >= 2) {
    const max = scored.reduce((a, b) => (b[1] > a[1] ? b : a));
    const min = scored.reduce((a, b) => (b[1] < a[1] ? b : a));
    lines.push(`Najwyższy wskaźnik: ${FACT_INDEX_NAMES[max[0]]}; najniższy: ${FACT_INDEX_NAMES[min[0]]}; rozstęp: ${max[1] - min[1]} pkt.`);
    const big: string[] = [];
    for (let i = 0; i < scored.length; i++) {
      for (let j = i + 1; j < scored.length; j++) {
        const d = Math.abs(scored[i][1] - scored[j][1]);
        if (d >= 15) big.push(`${abbr(scored[i][0])} vs ${abbr(scored[j][0])}: ${d} pkt`);
      }
    }
    lines.push(`Różnice między wskaźnikami ≥ 15 pkt (informacyjnie; program nie ocenia istotności statystycznej): ${big.length ? big.join('; ') : 'brak'}.`);
  }

  const entered = FACT_SUBTESTS.filter((s) => validScore(subtestScores[s.key]) !== null);
  if (entered.length === 0) {
    lines.push('Podtesty: wyniki podtestów nie zostały wprowadzone — opieraj się wyłącznie na wskaźnikach.');
  } else {
    lines.push('Podtesty (wyniki przeliczone, średnia 10, SD 3) — tylko kontekst, nie cytuj:');
    for (const s of entered) {
      const v = validScore(subtestScores[s.key])!;
      lines.push(`- ${s.name} (${abbr(s.index)}) = ${v} — ${getScoreInterpretation(v, 'subtest').toLowerCase()}`);
    }
    const missing = FACT_SUBTESTS.filter((s) => validScore(subtestScores[s.key]) === null);
    lines.push(`Brakujące podtesty / niepełne dane: ${missing.length
      ? missing.map((s) => `${s.name} (${abbr(s.index)}) — nie przeprowadzono, wskaźnik ${abbr(s.index)} interpretuj ostrożnie`).join('; ')
      : 'brak'}.`);
  }

  return lines.join('\n');
};

export interface PromptData {
  patientInfo: PatientInfo;
  subtestScores: SubtestScores;
  compositeScores: CompositeScores;
}

/** Formats a raw score string as "<score> (<interpretation>)" or 'N/A'. */
export const formatScoreWithInterpretation = (
  valStr: string | undefined,
  type: 'composite' | 'subtest'
): string => {
  if (!valStr) return 'N/A';
  const score = parseInt(valStr);
  if (isNaN(score) || score <= 0) return 'N/A';
  const interpretation = getScoreInterpretation(score, type);
  return `${score} (${interpretation})`;
};

/** Builds the comma-separated subtest list, e.g. "SI=12 (Przeciętny), SŁ=15 (Wysoki)". */
export const buildSubtestScoresList = (subtestScores: SubtestScores): string => {
  return Object.entries(subtestScores)
    .filter(([_, value]) => value && value !== '')
    .map(([key, value]) => {
      const score = parseInt(value);
      if (isNaN(score) || score <= 0) return `${key}=${value}`;
      const interpretation = getScoreInterpretation(score, 'subtest');
      return `${key}=${score} (${interpretation})`;
    })
    .join(', ');
};

/** Builds the human-readable summary of statistically significant discrepancies. */
export const buildDiscrepanciesText = (compositeScores: CompositeScores): string => {
  const discrepancies = calculateDiscrepancies(compositeScores);
  const significantDiscrepancies = discrepancies.filter(d => d.isSignificant);
  return significantDiscrepancies.length > 0
    ? significantDiscrepancies.map(d => d.message).join('\n')
    : 'Brak statystycznie istotnych rozbieżności (różnice < 15 pkt) między wskaźnikami złożonymi.';
};

/**
 * Replaces every supported placeholder in `promptText` with the values
 * derived from `data`. Behaviour is identical to the former
 * usePromptLibrary.replacePlaceholders implementation.
 */
export const replacePlaceholders = (promptText: string, data: PromptData): string => {
  const { patientInfo, subtestScores, compositeScores } = data;

  const subtestScoresList = buildSubtestScoresList(subtestScores);
  const discrepanciesText = buildDiscrepanciesText(compositeScores);

  // Facts block is built only when the template asks for it (the v2.1 prompt).
  const withFacts = promptText.includes(WISCV_FACTS_PLACEHOLDER)
    ? promptText.split(WISCV_FACTS_PLACEHOLDER).join(
        buildInterpretationFacts({ name: patientInfo.name, subtestScores, compositeScores })
      )
    : promptText;

  return withFacts
    .replace(/{PATIENT_NAME}/g, patientInfo.name || 'Dziecko')
    .replace(/{VCI_SCORE}/g, formatScoreWithInterpretation(compositeScores.vci, 'composite'))
    .replace(/{VSI_SCORE}/g, formatScoreWithInterpretation(compositeScores.vsi, 'composite'))
    .replace(/{FRI_SCORE}/g, formatScoreWithInterpretation(compositeScores.fri, 'composite'))
    .replace(/{WMI_SCORE}/g, formatScoreWithInterpretation(compositeScores.wmi, 'composite'))
    .replace(/{PSI_SCORE}/g, formatScoreWithInterpretation(compositeScores.psi, 'composite'))
    .replace(/{FSIQ_SCORE}/g, formatScoreWithInterpretation(compositeScores.fsiq, 'composite'))
    .replace(/{SUBTEST_SCORES}/g, subtestScoresList || 'Brak danych')
    .replace(/{DISCREPANCIES}/g, discrepanciesText);
};
